import React, { useCallback, useEffect, useState } from 'react';

import { Button, Flex, Modal, ModalProps, TextInput } from '@mantine/core';
import classNames from 'classnames';

import styles from './Modal.module.scss';

export interface ModalHeaderProps {
  title: string;
  className?: string;
  isEditable?: boolean;
  onEdit?: (value: string) => void;
}

export interface ModalButtonProps {
  text: React.ReactNode;
  icon?: React.ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  className?: string;
}

export interface CustomModalProps {
  header: ModalHeaderProps;
  footer?: React.ReactNode;
  modalProps?: Omit<ModalProps, 'children'>;
  primaryButton?: ModalButtonProps;
  secondaryButton?: ModalButtonProps;
  actionButton?: ModalButtonProps | null;
  className?: string;
  children?: React.ReactNode;
}

const CustomModal: React.FC<CustomModalProps> = ({
  header,
  children: modalContent,
  footer,
  modalProps,
  primaryButton,
  secondaryButton,
  actionButton,
  className
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(header.title);

  useEffect(() => {
    setEditableTitle(header.title);
  }, [header.title]);

  const closeModal = useCallback(() => {
    setIsEditing(false);
    modalProps?.onClose();
  }, [modalProps]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableTitle(e.currentTarget.value);
  };

  const handleTitleBlur = () => {
    setIsEditing(false);

    if (editableTitle.trim() === '') {
      setEditableTitle(header.title);
      return;
    }

    if (editableTitle !== header.title && header.onEdit) {
      header.onEdit(editableTitle);
    }
  };

  const handleTitleClick = () => {
    if (header.isEditable) {
      setIsEditing(true);
    }
  };

  const renderHeader = () => {
    const headerstyles = classNames(styles.modalHeader, header.className);

    const renderTitleContent = () => {
      // Case 1: Editable and currently being edited
      if (header.isEditable && isEditing) {
        return (
          <TextInput
            value={editableTitle}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            autoFocus
            className={styles.modalTitleInput}
          />
        );
      }

      // Case 2: Editable but not currently being edited
      if (header.isEditable) {
        return (
          <Modal.Title className={styles.modalTitle} onClick={handleTitleClick}>
            {editableTitle}
          </Modal.Title>
        );
      }

      // Case 3: Not editable
      return <div className={styles.modalTitleStatic}>{header.title}</div>;
    };

    return (
      <div className={headerstyles}>
        {renderTitleContent()}
        <Modal.CloseButton onClick={closeModal} className={styles.closeButton} />
      </div>
    );
  };

  const renderFooter = () => {
    if (footer) {
      return footer;
    }

    return (
      <Flex className={styles.footer}>
        {actionButton && (
          <Button
            onClick={actionButton.onClick}
            disabled={actionButton.isDisabled}
            loading={actionButton.isLoading}
            className={actionButton.className}
            leftSection={actionButton.icon}
            variant="transparent">
            {actionButton.text}
          </Button>
        )}
        <Flex className={styles.buttonGroup}>
          {secondaryButton && (
            <Button
              variant="outline"
              onClick={secondaryButton.onClick}
              disabled={secondaryButton.isDisabled}
              loading={secondaryButton.isLoading}
              className={classNames(styles.btn, secondaryButton.className)}
              leftSection={secondaryButton.icon}>
              {secondaryButton.text}
            </Button>
          )}
          {primaryButton && (
            <Button
              onClick={primaryButton.onClick}
              disabled={primaryButton.isDisabled}
              loading={primaryButton.isLoading}
              className={classNames(styles.btn, primaryButton.className)}
              leftSection={primaryButton.icon}>
              {primaryButton.text}
            </Button>
          )}
        </Flex>
      </Flex>
    );
  };

  return (
    <Modal.Root
      opened={modalProps?.opened || false}
      onClose={closeModal}
      className={className}
      {...modalProps}
      styles={{
        body: { padding: 0 }
      }}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header className={styles.modalHeaderContainer} bg={'plum.3'}>
          {renderHeader()}
        </Modal.Header>
        <Modal.Body>
          {modalContent}
          {renderFooter()}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

export default CustomModal;
